// src/lib/imageCompress.js

export async function compressImageFile(file, opts = {}) {
  const {
    maxWidth = 1600,
    maxHeight = 1600,
    mimeType = "image/webp",
    quality = 0.82,
    filenameBase = null,
  } = opts;

  if (!(file instanceof File)) {
    throw new Error("compressImageFile: file must be a File");
  }

  if (!file.type?.startsWith?.("image/")) {
    return { file, changed: false };
  }

  if (typeof createImageBitmap !== "function") {
    // Fallback: if createImageBitmap isn't available, skip compression.
    return { file, changed: false };
  }

  const bitmap = await createImageBitmap(file);

  const ratio = Math.min(maxWidth / bitmap.width, maxHeight / bitmap.height, 1);
  const targetW = Math.max(1, Math.round(bitmap.width * ratio));
  const targetH = Math.max(1, Math.round(bitmap.height * ratio));

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;

  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) throw new Error("compressImageFile: canvas context unavailable");

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, mimeType, quality));
  if (!blob) throw new Error("compressImageFile: toBlob failed");

  const base =
    filenameBase ||
    (file.name?.replace(/\.[^/.]+$/, "") || "image");

  const ext =
    mimeType === "image/webp"
      ? "webp"
      : mimeType === "image/jpeg"
      ? "jpg"
      : "img";

  const outFile = new File([blob], `${base}.${ext}`, { type: mimeType });

  return {
    file: outFile,
    changed: outFile.size !== file.size || outFile.type !== file.type,
    meta: {
      originalBytes: file.size,
      compressedBytes: outFile.size,
      originalWidth: bitmap.width,
      originalHeight: bitmap.height,
      width: targetW,
      height: targetH,
      mimeType,
      quality,
    },
  };
}

export async function makeThumbAndDisplay(file) {
  const base = file?.name?.replace(/\.[^/.]+$/, "") || "image";

  const thumb = await compressImageFile(file, {
    maxWidth: 600,
    maxHeight: 600,
    mimeType: "image/webp",
    quality: 0.78,
    filenameBase: `${base}-thumb`,
  });

  const display = await compressImageFile(file, {
    maxWidth: 1600,
    maxHeight: 1600,
    mimeType: "image/webp",
    quality: 0.84,
    filenameBase: `${base}-display`,
  });

  return { thumb, display };
}
